import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { ActionItemResponse, ActionItemCreate, ActionItemUpdate, ActionItemStatusUpdate } from "@/lib/types";
import api from "@/lib/api";
import { useOrganizationsStore } from "./organizationsStore";

interface ActionItemsState {
  actionItems: ActionItemResponse[];
  isLoading: boolean;
  error: string | null;
  // Fetch all action items for an organization
  fetchActionItems: (organizationId: string) => Promise<void>;
  // Fetch action items for the currently selected organization
  fetchActionItemsForCurrentOrg: () => Promise<void>;
  // Create a new action item
  createActionItem: (actionItem: ActionItemCreate) => Promise<ActionItemResponse | null>;
  // Update an existing action item
  updateActionItem: (actionItemId: string, data: ActionItemUpdate) => Promise<ActionItemResponse | null>;
  // Update just the status of an action item
  updateActionItemStatus: (actionItemId: string, data: ActionItemStatusUpdate) => Promise<ActionItemResponse | null>;
  // Get a specific action item by ID
  getActionItem: (actionItemId: string) => ActionItemResponse | undefined;
  // Clear action items (e.g., when logging out)
  clearActionItems: () => void;
}

export const useActionItemsStore = create<ActionItemsState>()(
  immer((set, get) => ({
    actionItems: [],
    isLoading: false,
    error: null,

    fetchActionItems: async (organizationId: string) => {
      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      try {
        const response = await api.getOrganizationActionItems(organizationId);
        set((state) => {
          state.actionItems = response.items;
          state.isLoading = false;
        });
      } catch (error) {
        console.error("Failed to fetch action items:", error);
        set((state) => {
          state.error = "Failed to fetch action items";
          state.isLoading = false;
        });
      }
    },

    fetchActionItemsForCurrentOrg: async () => {
      const selectedOrgId =
        useOrganizationsStore.getState().selectedOrganizationId;

      if (!selectedOrgId) {
        set((state) => {
          state.error = "No organization selected";
        });
        return;
      }

      await get().fetchActionItems(selectedOrgId);
    },

    createActionItem: async (actionItem: ActionItemCreate) => {
      set((state) => {
        state.error = null;
      });

      try {
        const newActionItem = await api.createActionItem(actionItem);
        set((state) => {
          state.actionItems.push(newActionItem);
        });
        return newActionItem;
      } catch (error) {
        console.error("Failed to create action item:", error);
        set((state) => {
          state.error = "Failed to create action item";
        });
        return null;
      }
    },

    updateActionItem: async (actionItemId: string, data: ActionItemUpdate) => {
      set((state) => {
        state.error = null;
      });

      try {
        const updatedActionItem = await api.updateActionItem(actionItemId, data);
        set((state) => {
          const index = state.actionItems.findIndex((item) => item.id === actionItemId);
          if (index !== -1) {
            state.actionItems[index] = updatedActionItem;
          }
        });
        return updatedActionItem;
      } catch (error) {
        console.error("Failed to update action item:", error);
        set((state) => {
          state.error = "Failed to update action item";
        });
        return null;
      }
    },

    updateActionItemStatus: async (actionItemId: string, data: ActionItemStatusUpdate) => {
      set((state) => {
        state.error = null;
      });

      try {
        const updatedActionItem = await api.updateActionItemStatus(actionItemId, data);
        set((state) => {
          const index = state.actionItems.findIndex((item) => item.id === actionItemId);
          if (index !== -1) {
            state.actionItems[index] = updatedActionItem;
          }
        });
        return updatedActionItem;
      } catch (error) {
        console.error("Failed to update action item status:", error);
        set((state) => {
          state.error = "Failed to update action item status";
        });
        return null;
      }
    },

    getActionItem: (actionItemId) => {
      return get().actionItems.find((item) => item.id === actionItemId);
    },

    clearActionItems: () => {
      set((state) => {
        state.actionItems = [];
        state.error = null;
      });
    },
  }))
);